const TOAST_DURATION = 6000;
const TOAST_MAX_VISIBLE = 5;
const VALID_TYPES = ['success', 'error', 'info'];

/* @ngInject */
export default class SonnerToastService {
    constructor($rootScope, $timeout) {
        this.$rootScope = $rootScope;
        this.$timeout = $timeout;
        this.toasts = [];
        this.idCounter = 0;
        this.container = null;
        this.isHovered = false;

        // Cleanup on app teardown
        this.$rootScope.$on('$destroy', () => this.destroy());
    }

    ensureContainer() {
        if (this.container) return;

        this.container = document.createElement('div');
        this.container.className = 'sonner-container';
        document.body.appendChild(this.container);

        this.container.addEventListener('mouseenter', () => this.onMouseEnter());
        this.container.addEventListener('mouseleave', () => this.onMouseLeave());
    }

    destroy() {
        if (this.container) {
            this.container.remove();
            this.container = null;
        }
        this.toasts = [];
    }

    show({ title, description, type = 'success', duration = TOAST_DURATION }) {
        const safeType = VALID_TYPES.indexOf(type) !== -1 ? type : 'info';
        this.ensureContainer();

        const id = ++this.idCounter;
        const toast = { id, title, description, type: safeType, removing: false };
        this.toasts.push(toast);

        // Trim old toasts beyond max
        while (this.toasts.length > TOAST_MAX_VISIBLE) {
            this.toasts.shift();
        }

        this.render();

        // Schedule removal
        toast.timeoutPromise = this.$timeout(() => {
            if (!this.isHovered) {
                this.dismiss(id);
            } else {
                // Will be dismissed on mouse leave
                toast.pendingDismiss = true;
            }
        }, duration);

        return id;
    }

    success(title, description) {
        return this.show({ title, description, type: 'success' });
    }

    error(title, description) {
        return this.show({ title, description, type: 'error' });
    }

    info(title, description) {
        return this.show({ title, description, type: 'info' });
    }

    dismiss(id) {
        const toast = this.toasts.find(t => t.id === id);
        if (!toast || toast.removing) return;

        toast.removing = true;
        this.render();

        // Remove from array after exit animation
        this.$timeout(() => {
            this.toasts = this.toasts.filter(t => t.id !== id);
            this.render();
        }, 400);
    }

    onMouseEnter() {
        this.isHovered = true;
        this.render();
    }

    onMouseLeave() {
        this.isHovered = false;
        this.render();

        // Dismiss any toasts that were waiting
        this.toasts.forEach(toast => {
            if (toast.pendingDismiss) {
                this.$timeout(() => this.dismiss(toast.id), 800);
            }
        });
    }

    render() {
        if (!this.container) return;

        const visibleToasts = this.toasts.slice();
        const count = visibleToasts.length;

        // Build HTML
        const html = visibleToasts.map((toast, i) => {
            const reverseIndex = count - 1 - i; // 0 = front (newest)
            const isFront = reverseIndex === 0;

            let transformStyle;
            if (this.isHovered) {
                // Fan out: stack upward with full size
                const yOffset = reverseIndex * -64;
                transformStyle = `translateY(${yOffset}px) scale(1)`;
            } else {
                // Compressed stack
                const yOffset = reverseIndex * -8;
                const scale = 1 - (reverseIndex * 0.04);
                transformStyle = `translateY(${yOffset}px) scale(${scale})`;
            }

            const isHidden = !toast.removing && reverseIndex > 2 && !this.isHovered;
            const opacityStyle = toast.removing || isHidden ? '0' : '1';
            const pointerEvents = isFront || this.isHovered ? 'auto' : 'none';

            const icon = this.getIcon(toast.type);

            return `<div class="sonner-toast sonner-toast--${toast.type} ${toast.removing ? 'sonner-toast--removing' : ''}"
                        style="transform: ${transformStyle}; opacity: ${opacityStyle}; pointer-events: ${pointerEvents}; z-index: ${1000 + i};"
                        data-toast-id="${toast.id}">
                        <div class="sonner-toast__icon">${icon}</div>
                        <div class="sonner-toast__content">
                            <div class="sonner-toast__title">${this.escapeHtml(toast.title)}</div>
                            ${toast.description ? `<div class="sonner-toast__description">${this.escapeHtml(toast.description)}</div>` : ''}
                        </div>
                        <button class="sonner-toast__close" data-dismiss-id="${toast.id}">&times;</button>
                    </div>`;
        }).join('');

        this.container.innerHTML = html;

        // Bind close buttons
        this.container.querySelectorAll('[data-dismiss-id]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const dismissId = parseInt(btn.getAttribute('data-dismiss-id'), 10);
                this.dismiss(dismissId);
            });
        });
    }

    getIcon(type) {
        switch (type) {
            case 'success':
                return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#2e7d32"/><path d="M5 8l2 2 4-4" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            case 'error':
                return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#c62828"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/></svg>';
            case 'info':
                return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#1565c0"/><path d="M8 7v4M8 5v.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/></svg>';
            default:
                return '';
        }
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
